/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { styled } from '@mui/system';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import ReactSpeedometer from 'react-d3-speedometer';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  getLoadFactorHistory,
  postLastLoadCharge,
  postLoadCharge,
} from 'js/actions/groupDashboardActions';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import {
  Portlet,
  PortletContent,
  PortletHeader,
  PortletLabel,
  PortletToolbar,
} from 'components';
import { Download, ShareButton } from 'components/Download';

// 📊 Registrar módulos de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// 🎨 Estilos personalizados para el título del diálogo
const StyledDialogTitle = styled(DialogTitle)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const LoadFactor = ({ chargeId, year, month }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const chartRef = useRef();
  const chartIntRef = useRef();

  // 🔄 Obtener datos del store
  const loadFactorValue = useSelector(
    (state) => state.groupDashboard.loadCharge
  );
  const lastLoadFactorValue = useSelector(
    (state) => state.groupDashboard.lastLoadCharge
  );
  const loadHistory = useSelector((state) => state.groupDashboard.loadHistory);

  // 📅 Meses traducidos
  const meses = useMemo(
    () =>
      [
        'jan',
        'feb',
        'mar',
        'apr',
        'may',
        'jun',
        'jul',
        'ago',
        'sep',
        'oct',
        'nov',
        'dec',
      ].map((id) => intl.formatMessage({ id: `app.months.${id}` })),
    [intl]
  );

  const lastMonth = () => {
    const today = new Date();
    const currentMonth = today.getMonth();
    // Si month es null, tomar el mes actual (currentMonth)
    const selectedMonth =
      month === null || month === undefined ? currentMonth + 1 : month;

    // Calcular el mes anterior (considerando enero como caso especial)
    const previousMonthIndex = selectedMonth === '01' ? 11 : selectedMonth - 2;

    // Devolver el nombre del mes anterior
    return meses[previousMonthIndex];
  };

  // 📈 Formatear datos históricos
  const formattedData = useMemo(
    () =>
      loadHistory?.map((item) => ({
        month: parseInt(item.month.slice(-2), 10),
        value: parseFloat(item.load_factor).toFixed(2),
        prom: parseFloat(item.promkwh).toFixed(2),
      })),
    [loadHistory]
  );

  const mesesOrdenados = formattedData
    ?.map((item) => meses[item.month - 1])
    .reverse();
  const datos = formattedData?.map((item) => parseFloat(item.value)).reverse();
  const dataPrice = formattedData
    ?.map((item) => parseFloat(item.prom))
    .reverse();

  // 📅 Cargar datos cuando cambia el mes o año
  useEffect(() => {
    const currentYear = year ?? new Date().getFullYear();
    const currentMonth = month ?? new Date().getMonth() + 1;

    const startDate = format(
      startOfMonth(new Date(currentYear, currentMonth - 1)),
      'yyyyMMdd'
    );
    const endDate = format(
      endOfMonth(new Date(currentYear, currentMonth - 1)),
      'yyyyMMdd'
    );

    const lastStartDate = format(
      startOfMonth(subMonths(new Date(currentYear, currentMonth - 1), 1)),
      'yyyyMMdd'
    );
    const lastEndDate = format(
      endOfMonth(subMonths(new Date(currentYear, currentMonth - 1), 1)),
      'yyyyMMdd'
    );

    dispatch(postLoadCharge(startDate, endDate, chargeId));
    dispatch(postLastLoadCharge(lastStartDate, lastEndDate, chargeId));
  }, [year, month, chargeId, dispatch]);

  // 📈 Opciones del gráfico
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      'y-axis-1': {
        position: 'left',
        ticks: {
          callback: function (value) {
            return `${value}%`;
          },
        },
      },
      'y-axis-2': {
        position: 'right',
        ticks: {
          callback: function (value) {
            return `$${value.toFixed(2)}`;
          }, // Si los precios son en dólares
        },
      },
    },
  };

  // 🔄 Datos para el gráfico
  const data = {
    labels: mesesOrdenados,
    datasets: [
      {
        label: intl.formatMessage({
          id: 'app.charge.board.loadFactor.load.title',
        }),
        data: datos,
        borderColor: '#4caf50', // Verde para la carga
        backgroundColor: 'rgba(76, 175, 80, 0.2)', // Fondo verde claro
        fill: true, // Para mostrar un área rellena
        yAxisID: 'y-axis-1', // Usamos el primer eje Y
      },
      {
        label: intl.formatMessage({
          id: 'app.charge.board.loadFactor.price.title',
        }),
        data: dataPrice,
        borderColor: '#f44336', // Rojo para el precio
        backgroundColor: 'rgba(244, 67, 54, 0.2)', // Fondo rojo claro
        fill: false, // Sin área rellena
        yAxisID: 'y-axis-2', // Usamos el segundo eje Y
      },
    ],
  };

  // 🔄 Función para abrir historial
  const handleHistory = () => {
    dispatch(getLoadFactorHistory(chargeId));
    setOpen(true);
  };

  // ❌ Función para cerrar el diálogo
  const handleClose = () => {
    setOpen(false);
  };

  const formatPercentage = (value) => {
    if (value == null) {
      return '0.00';
    }
    return value.toFixed(2);
  };

  const segmentsStops = [0, 54, 70, 100];

  const segmentFormatter = (value) => {
    return `${value}%`;
  };

  return (
    <div style={{ height: '100%' }} ref={chartRef}>
      <Portlet>
        <PortletHeader noDivider>
          <PortletLabel
            title={intl.formatMessage({ id: 'app.deepview.loadFactor.title' })}
          />
          <PortletToolbar style={{ display: 'flex', gap: 8 }}>
            <Download
              itemRef={chartRef}
              name={intl.formatMessage({ id: 'app.deepview.loadFactor.title' })}
            />
            <ShareButton
              itemRef={chartRef}
              name={intl.formatMessage({ id: 'app.deepview.loadFactor.title' })}
            />
          </PortletToolbar>
        </PortletHeader>
        <PortletContent
          sx={{
            fontFamily: 'Lato, Helvetica Neue, Arial',
            gap: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 2,
              fontFamily: 'Lato, Helvetica Neue, Arial',
              gap: 2,
            }}
          >
            <Typography variant="h5" sx={{ mb: 1 }}>
              {intl.formatMessage({
                id: 'app.meter.board.fpVariationInMonth.dataSet.labels.currMonth',
              })}
            </Typography>
            <ReactSpeedometer
              currentValueText={`${formatPercentage(loadFactorValue) || 0}%`}
              customSegmentStops={segmentsStops}
              forceRender
              height={125}
              labelFontSize={'12px'}
              maxValue={100}
              minValue={0}
              needleColor={'#000'}
              needleHeightRatio={0.6}
              ringWidth={40}
              segmentColors={['#EB0000', '#FAC800', '#00C454']}
              segments={3}
              segmentValueFormatter={segmentFormatter}
              textColor={'#000'}
              value={formatPercentage(loadFactorValue) || 0}
              valueFormat=".1f"
              valueTextFontSize={'20px'}
              valueTextFontWeight={'600'}
              width={200}
            />
            <Chip
              label={`${intl.formatMessage({ id: 'app.meter.board.loadFactor.closeLabel' })} ${lastMonth()}`}
              sx={{ color: '#FFF', fontWeight: 600, bgcolor: '#730065' }}
            />
            <Typography
              variant="h4"
              sx={{ fontWeight: 600, textAlign: 'center' }}
            >
              {lastLoadFactorValue
                ? `${parseFloat(lastLoadFactorValue).toFixed(2)}%`
                : '%'}
            </Typography>
            <Button
              color="secondary"
              onClick={handleHistory}
              sx={{ mb: 2 }}
              variant="contained"
            >
              {intl.formatMessage({ id: 'app.charge.board.loadFactor.button' })}
            </Button>
          </Box>
        </PortletContent>
        <Dialog
          fullWidth
          maxWidth="xl"
          onClose={handleClose}
          open={open}
          scroll="paper"
        >
          <StyledDialogTitle>
            <Chip
              label={intl.formatMessage({
                id: 'app.charge.board.loadFactor.title',
              })}
              sx={{ color: '#FFF', fontWeight: 600, bgcolor: '#730065' }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Download
                itemRef={chartIntRef}
                name={intl.formatMessage({
                  id: 'app.charge.board.loadFactor.title"',
                })}
              />
              <ShareButton
                itemRef={chartIntRef}
                name={intl.formatMessage({
                  id: 'app.charge.board.loadFactor.title"',
                })}
              />
            </Box>
          </StyledDialogTitle>
          <DialogContent ref={chartIntRef}>
            <Paper sx={{ p: 2, width: '100%', height: 450 }}>
              {datos ? (
                <Line data={data} options={options} height={450} />
              ) : (
                <CircularProgress />
              )}
            </Paper>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} variant="contained" color="secondary">
              {intl.formatMessage({ id: 'app.deepview.demand.btnCancel' })}
            </Button>
          </DialogActions>
        </Dialog>
      </Portlet>
    </div>
  );
};

export default LoadFactor;
